/**
 * googleAuth — thin wrapper around Google Identity Services (GSI) that
 * lets a fully custom "Continue with Google" button (X-style) trigger
 * Google's account-chooser popup and receive an ID token (JWT).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * How it works
 * ------------
 * GSI's `google.accounts.id` returns an ID token via a callback but
 * only exposes two triggers for it:
 *   1. `prompt()` — displays the small One-Tap widget (often blocked by
 *      third-party cookies / FedCM and cannot be relied on).
 *   2. `renderButton(el, opts)` — draws Google's own signed-in button.
 *      Clicking that button DOES open the reliable account chooser
 *      popup and DOES fire the callback with the ID token.
 *
 * To keep the app's custom X-style button UI while still getting the
 * reliable popup, we (a) render Google's real button into a hidden
 * off-screen div, (b) forward clicks from the visible custom button to
 * the hidden Google button. This is Google's officially-supported
 * customization pattern for consent-safe ID-token flows.
 *
 * Consumers use `useGoogleSignIn({ onCredential })`.
 *   • `ready`      — GSI is loaded & the hidden button is rendered.
 *   • `signIn()`   — call this from your visible button's onClick.
 *   • `renderRef`  — attach to a hidden div; the invisible Google
 *                    button will live inside it (we absolute-position
 *                    it off-screen).
 *   • `error`      — populated when GSI fails to load (blocked by
 *                    network, ad-blocker, wrong client_id, etc.).
 */
import { useCallback, useEffect, useRef, useState } from "react";

const CLIENT_ID = process.env.REACT_APP_Google_Client_ID_Web;
const GSI_SRC = "https://accounts.google.com/gsi/client";

let gsiPromise = null;

function loadGsi() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.google?.accounts?.id) return Promise.resolve(window.google);
  if (gsiPromise) return gsiPromise;
  gsiPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GSI_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google));
      existing.addEventListener("error", () => reject(new Error("GSI failed to load")));
      return;
    }
    const s = document.createElement("script");
    s.src = GSI_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.google);
    s.onerror = () => reject(new Error("GSI failed to load"));
    document.head.appendChild(s);
  });
  return gsiPromise;
}

/**
 * useGoogleSignIn — React hook.
 *
 * @param {object} opts
 * @param {(credential: string, profile: object) => void} opts.onCredential
 *        Called with the raw ID-token JWT string and the decoded JWT
 *        payload (email, name, picture, sub) as soon as Google returns.
 * @param {(err: Error) => void} [opts.onError]
 *
 * @returns {{
 *   ready: boolean,
 *   error: Error|null,
 *   signIn: () => void,
 *   renderRef: React.RefObject<HTMLDivElement>,
 * }}
 */
export function useGoogleSignIn({ onCredential, onError } = {}) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const renderRef = useRef(null);
  const onCredentialRef = useRef(onCredential);
  const onErrorRef = useRef(onError);
  onCredentialRef.current = onCredential;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!CLIENT_ID) {
      const err = new Error("REACT_APP_Google_Client_ID_Web is not set");
      setError(err);
      onErrorRef.current && onErrorRef.current(err);
      return;
    }
    let cancelled = false;
    loadGsi()
      .then((google) => {
        if (cancelled) return;
        try {
          google.accounts.id.initialize({
            client_id: CLIENT_ID,
            callback: (resp) => {
              if (!resp?.credential) return;
              try {
                const [, payloadB64] = resp.credential.split(".");
                const profile = JSON.parse(
                  atob(payloadB64.replace(/-/g, "+").replace(/_/g, "/"))
                );
                onCredentialRef.current &&
                  onCredentialRef.current(resp.credential, profile);
              } catch {
                onCredentialRef.current &&
                  onCredentialRef.current(resp.credential, null);
              }
            },
            auto_select: false,
            cancel_on_tap_outside: true,
            ux_mode: "popup",
          });
          if (renderRef.current) {
            google.accounts.id.renderButton(renderRef.current, {
              type: "standard",
              theme: "outline",
              size: "large",
              text: "continue_with",
              shape: "pill",
              logo_alignment: "left",
              width: 300,
            });
          }
          setReady(true);
        } catch (e) {
          setError(e);
          onErrorRef.current && onErrorRef.current(e);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
        onErrorRef.current && onErrorRef.current(e);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(() => {
    if (!renderRef.current) return;
    // Google's <div role="button"> lives one level inside the wrapper.
    const btn =
      renderRef.current.querySelector('div[role="button"]') ||
      renderRef.current.querySelector("button") ||
      renderRef.current.firstElementChild;
    if (btn && typeof btn.click === "function") {
      btn.click();
      return;
    }
    // Fallback — show One-Tap prompt (may be silently skipped by GSI).
    try {
      window.google?.accounts?.id?.prompt();
    } catch {
      /* ignore */
    }
  }, []);

  return { ready, error, signIn, renderRef };
}
